import { Catch } from './catch.model';
import type { FishingEvent } from './fishing-event.model';
import type { TrawlSet } from './trawl-set.model';

/**
 * Shared species roll-up used by the summary screens.
 *
 * HMS records catch weight as the AVERAGE weight per fish, not a total, so any HMS roll-up has to
 * multiply by the piece count before summing: total = the sum of (count * avgWeight) over every
 * catch record. HMS sets are one per fishing day, so this is the same as totalling each fishing day
 * and adding the days together -- doing it per catch record just also stays correct if a single day
 * ever holds more than one set.
 *
 * Groundfish and Trawl record real weights and are summed as-is.
 */

/** Trawl sets carry no `fishery` field, so they fall through as non-HMS. */
function isHMSSet(set: FishingEvent | TrawlSet): boolean {
    return (set as FishingEvent).fishery === 'HMS';
}

/**
 * The weight a single catch record contributes to the roll-up.
 * `undefined` means no weight was recorded -- HMS avg weight is optional, and it is entered on
 * non-Harpoon gear even though the submission payload drops it there.
 */
function weightContribution(weight: number | undefined, count: number | undefined, isHMS: boolean): number | undefined {
    if (weight === undefined || weight === null) {
        return undefined;
    }
    return isHMS ? weight * (count ?? 0) : weight;
}

/** Adds a contribution while keeping "nothing recorded" (undefined) distinct from a recorded zero. */
function addWeight(total: number | undefined, contribution: number | undefined): number | undefined {
    if (contribution === undefined) {
        return total;
    }
    return (total ?? 0) + contribution;
}

/**
 * Totals catch across the given sets, one row per species.
 * @param sets Sets to roll up. May mix fisheries -- each set is treated according to its own fishery.
 * @returns Freshly built {@link Catch} rows; the source sets are never mutated.
 */
export function aggregateCatchBySpecies(sets: (FishingEvent | TrawlSet)[]): Catch[] {
    const bySpecies = new Map<string, Catch>();

    sets.forEach(set => {
        const isHMS = isHMSSet(set);

        set.catchItems.forEach(ci => {
            const retained = weightContribution(ci.retainedWeight, ci.retainedCount, isHMS);
            const released = weightContribution(ci.releasedWeight, ci.releasedCount, isHMS);
            const existing = bySpecies.get(ci.species.name);

            if (!existing) {
                // Work on a copy so the aggregate never writes back into stored trip data.
                const row = Catch.transformAnonymous([ci])[0];
                row.retainedWeight = retained;
                row.releasedWeight = released;
                bySpecies.set(ci.species.name, row);
                return;
            }

            existing.retainedCount = (existing.retainedCount ?? 0) + (ci.retainedCount ?? 0);
            existing.releasedCount = (existing.releasedCount ?? 0) + (ci.releasedCount ?? 0);
            existing.retainedWeight = addWeight(existing.retainedWeight, retained);
            existing.releasedWeight = addWeight(existing.releasedWeight, released);
        });
    });

    return [...bySpecies.values()];
}
